import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import InchargeLayout from './InchargeLayout';
import { FaArrowLeft, FaChevronRight } from 'react-icons/fa';

const ViewApplicationForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const userData = location.state?.userData || null;

    useEffect(() => {
        if (!userData) {
            navigate('/main-view-subhe-form');
        }
    }, [userData, navigate]);

    if (!userData) return null;

    const InputField = ({ label, value, type = "text", required = false, placeholder = "" }) => (
        <div className="flex flex-col space-y-1.5">
            <label className="text-sm font-medium text-gray-600">
                {label} {required && <span className="text-red-500">*</span>}
            </label>
            <input
                type={type}
                readOnly
                value={value || ""}
                placeholder={placeholder}
                className="w-full bg-gray-50 border border-gray-300 rounded px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-blue-500/10 transition-all"
            />
        </div>
    );

    const SelectField = ({ label, value, required = false }) => (
        <div className="flex flex-col space-y-1.5">
            <label className="text-sm font-medium text-gray-600">
                {label} {required && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
                <select disabled className="w-full bg-gray-50 border border-gray-300 rounded px-3 py-2 text-sm text-gray-800 outline-none appearance-none cursor-not-allowed">
                    <option>{value || "N/A"}</option>
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                </div>
            </div>
        </div>
    );

    const TextAreaField = ({ label, value, required = false }) => (
        <div className="flex flex-col space-y-1.5 col-span-full">
            <label className="text-sm font-medium text-gray-600">
                {label} {required && <span className="text-red-500">*</span>}
            </label>
            <textarea
                readOnly
                value={value || ""}
                rows={3}
                className="w-full bg-gray-50 border border-gray-300 rounded px-3 py-2 text-sm text-gray-800 outline-none resize-none"
            />
        </div>
    );

    return (
        <InchargeLayout>
            <div className="min-h-screen bg-gray-50 p-4 md:p-6 text-gray-900">
                {/* Header & Breadcrumb */}
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate(-1)}
                            className="p-2 bg-white border border-gray-300 rounded-lg text-gray-600 hover:text-blue-600 hover:border-blue-300 hover:bg-blue-50 transition-all shadow-sm"
                        >
                            <FaArrowLeft size={14} />
                        </button>
                        <h1 className="text-xl font-semibold text-gray-800">अर्ज फॉर्म पहा</h1>
                    </div>
                    <div className="flex items-center text-xs font-medium text-gray-500 gap-2">
                        <span>सांगली जिल्हा परिषद</span>
                        <FaChevronRight size={8} className="text-gray-400" />
                        <span className="text-blue-600">अर्ज फॉर्म पहा</span>
                    </div>
                </div>

                {/* Main Form Content */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-6 md:p-8 space-y-8">

                        {/* Grid Rows */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

                            <InputField label="अर्जदार आडनाव" value={userData.surname} required />
                            <InputField label="पहिले नाव" value={userData.firstName} required />
                            <InputField label="वडीलांचे नाव" value={userData.middleName} required />

                            <InputField label="व्हॉट्सॲप नंबर" value={userData.whatsappNo} required />
                            <InputField label="ई - मेल आयडी" value={userData.email} required />
                            <InputField label="जन्मतारीख" value={userData.dob} type="date" />

                            <InputField label="जन्म स्थान" value={userData.birthPlace} />
                            <SelectField label="तालुका" value={userData.taluka} required />
                            <SelectField label="तांत्रिक पात्रता" value={userData.qualification} required />

                            <SelectField label="श्रेणी" value={userData.category} required />
                            <InputField label="आधार क्रमांक" value={userData.aadharNo} />
                            <InputField label="पॅन क्रमांक" value={userData.panNo} />

                            <InputField label="जी.एस.टी क्रमांक" value={userData.gstNo} placeholder="जी.एस.टी क्रमांक प्रविष्ट करा" />
                            <InputField label="संस्थेचे नाव" value={userData.firmName} placeholder="संस्थेचे नाव प्रविष्ट करा" />
                            <InputField label="विद्यापीठाचे नाव" value={userData.university} />

                            <InputField label="उत्तीर्ण वर्ष" value={userData.passingYear} placeholder="उत्तीर्ण वर्ष प्रविष्ट करा" />
                            <InputField label="व्यवसायाचे ठिकाण" value={userData.businessPlace} required />
                            <InputField label="बँकेचे नाव" value={userData.bankName} required />

                            <TextAreaField label="राहण्याचा पत्ता" value={userData.address} required />

                            <InputField label="बँकेचा पत्ता" value={userData.bankAddress} />
                        </div>

                        {/* Footer Buttons */}
                        <div className="pt-8 border-t border-gray-100 flex items-center gap-3">
                            <button className="bg-blue-600 text-white px-6 py-2 rounded text-sm font-medium hover:bg-blue-700 transition">
                                Update Profile
                            </button>
                            <button
                                onClick={() => navigate(-1)}
                                className="bg-emerald-600 text-white px-6 py-2 rounded text-sm font-medium hover:bg-emerald-700 transition"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>

                {/* Copyright */}
                <div className="mt-8 text-center pb-8">
                    <p className="text-[11px] text-gray-400 font-medium">
                        © 2026 सांगली जिल्हा परिषद. सर्व हक्क राखीव.
                    </p>
                </div>
            </div>
        </InchargeLayout>
    );
};

export default ViewApplicationForm;
